import React from 'react';
import { twMerge } from 'tailwind-merge';

// ---------------------------------------------------------------------------
// SegmentedControl — inline segmented option-picker for 2-N mutually-exclusive
// options (e.g. "7d / 30d / 90d", "2 dice / 3 dice"). Distinct from Toggle
// (boolean switch) and FilterPill (filter-list pills). Uses radiogroup/radio
// ARIA pattern since exactly one option is always active.
// ---------------------------------------------------------------------------

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md' | 'lg';
  /**
   * Active-segment color scheme.
   * - `'neutral'` (default): active segment is surface-colored with a shadow — blends into the UI.
   * - `'pillar'`: active segment fills with a brand color + white text — use for primary navigation
   *   or feature-level selectors where the selection should pop. Pair with `pillarColor` to pick
   *   which brand color fills the active segment.
   */
  tone?: 'neutral' | 'pillar';
  /**
   * Which brand color fills the active segment when `tone="pillar"`.
   * - `'accent'` (default): copper accent (`bg-accent`, `var(--theme-accent)`).
   * - `'aegean'`: steel-blue aegean (`bg-aegean`, `#2C5F7C`) — use for info/connect pillar controls.
   * Only takes effect when `tone="pillar"`.
   */
  pillarColor?: 'accent' | 'aegean';
  /** Accessible label for the control group. */
  'aria-label'?: string;
  /**
   * Stretches the control to fill its container width with each segment
   * sharing equal space (`flex-1`), instead of the default content-width
   * inline-flex row. Use for mobile-required full-width tab pairs (e.g. a
   * 2-way Feed/Challenges switch under a channel header at <768px).
   */
  fullWidth?: boolean;
  className?: string;
}

const scSizeClasses: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-7 px-2.5 text-xs',
  md: 'h-9 px-3 text-sm',
  // Spec 242: mobile-context 44px touch target — md (h-9/36px) is below the
  // 44px mandatory minimum (docs/design/design-standards.md). h-11 = 2.75rem
  // = 44px in Tailwind's default scale, matching every other 44px touch
  // target in the codebase (e.g. CreateTaskV2's date/time inputs).
  lg: 'h-11 px-4 text-sm',
};

type PillarColor = NonNullable<SegmentedControlProps['pillarColor']>;

const pillarActiveClass: Record<PillarColor, string> = {
  accent: 'bg-accent text-white shadow-sm',
  aegean: 'bg-aegean text-white shadow-sm',
};

export function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  size = 'md',
  tone = 'neutral',
  pillarColor = 'accent',
  'aria-label': ariaLabel,
  fullWidth = false,
  className,
}: SegmentedControlProps<T>) {
  // ARROW-KEY NAVIGATION — required by the ARIA radiogroup pattern (Spec 321
  // accessibility criterion, assessed 2026-09-21). `role="radiogroup"` promises a
  // keyboard contract: arrows move between options and select as they go, and the
  // group is a single tab stop. Without it a screen-reader or keyboard-only user can
  // reach the control and then cannot change it, which is worse than an unlabelled
  // plain button group because the role advertises behaviour that is not there.
  //
  // Roving tabindex: only the selected option is tabbable, matching the pattern.
  const enabled = options.filter((o) => !o.disabled);

  // FOCUS FOLLOWS SELECTION — required by the same WAI-ARIA APG radiogroup pattern
  // as the keyboard handling above (helm-skeptical-evaluator, 2026-09-22, on the
  // original keyboard-nav fix). Changing `value` alone moves the roving tabIndex to
  // the new button on the NEXT render, but does not move DOM focus there — the
  // previously-focused (now tabIndex=-1) button keeps browser focus, so a screen
  // reader does not announce the new selection and the visible focus ring sits on a
  // button that is no longer in the tab sequence. Calling .focus() on the target
  // button works even while it is still tabIndex=-1 in the current render —
  // programmatic focus is not gated by tabIndex, only Tab-key traversal is — so this
  // is safe to call synchronously in the same handler that changes `value`.
  const buttonRefs = React.useRef<Map<T, HTMLButtonElement>>(new Map());
  const selectAndFocus = (val: T) => {
    onChange(val);
    buttonRefs.current.get(val)?.focus();
  };

  const moveSelection = (dir: 1 | -1) => {
    if (enabled.length === 0) return;
    const currentIdx = enabled.findIndex((o) => o.value === value);
    // Wraps, as the pattern specifies. An unknown current value starts at the edge.
    const nextIdx = currentIdx === -1
      ? (dir === 1 ? 0 : enabled.length - 1)
      : (currentIdx + dir + enabled.length) % enabled.length;
    selectAndFocus(enabled[nextIdx].value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        moveSelection(1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        moveSelection(-1);
        break;
      case 'Home':
        if (enabled.length) { e.preventDefault(); selectAndFocus(enabled[0].value); }
        break;
      case 'End':
        if (enabled.length) { e.preventDefault(); selectAndFocus(enabled[enabled.length - 1].value); }
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={twMerge(
        fullWidth ? 'flex w-full' : 'inline-flex items-center',
        'bg-bg-secondary rounded-lg p-0.5 gap-0.5',
        className,
      )}
    >
      {options.map((opt) => {
        const isActive = opt.value === value;
        return (
          <button
            key={opt.value}
            ref={(el) => {
              if (el) buttonRefs.current.set(opt.value, el);
              else buttonRefs.current.delete(opt.value);
            }}
            type="button"
            role="radio"
            aria-checked={isActive}
            // Roving tabindex — the group is ONE tab stop; arrows move within it.
            tabIndex={isActive ? 0 : -1}
            disabled={opt.disabled}
            onClick={() => !opt.disabled && onChange(opt.value)}
            className={twMerge(
              'inline-flex items-center justify-center rounded-md font-medium transition-colors cursor-pointer border-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1',
              'disabled:opacity-50 disabled:pointer-events-none',
              fullWidth && 'flex-1',
              scSizeClasses[size],
              isActive
                ? tone === 'pillar'
                  ? pillarActiveClass[pillarColor]
                  : 'bg-surface text-text-primary shadow-sm'
                : 'bg-transparent text-text-secondary hover:text-text-primary',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toggle — boolean on/off switch (role="switch"). See SegmentedControl above
// for the n-way option-picker.
// ---------------------------------------------------------------------------

export interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
  /**
   * Accessible name for the switch when a visible label is rendered elsewhere
   * (e.g. a sibling heading/list-item text) and the internal `label` row would
   * be redundant. Falls back to `label` when omitted. Without one of the two,
   * the switch has no accessible name at all.
   */
  ariaLabel?: string;
  disabled?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const sizeConfig = {
  sm: { track: 'w-8 h-[18px]', thumb: 'w-3.5 h-3.5', translate: 'translate-x-[14px]' },
  md: { track: 'w-11 h-6', thumb: 'w-5 h-5', translate: 'translate-x-5' },
};

export function Toggle({
  checked,
  onChange,
  label,
  description,
  ariaLabel,
  disabled = false,
  size = 'md',
  className,
}: ToggleProps) {
  const sizes = sizeConfig[size];

  const toggle = (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel ?? label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={twMerge(
        'relative inline-flex shrink-0 rounded-full border-none cursor-pointer transition-colors duration-200',
        'focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
        sizes.track,
        checked ? 'bg-accent' : 'bg-text-tertiary',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      <span
        className={twMerge(
          'pointer-events-none inline-block rounded-full bg-white shadow-sm transition-transform duration-200',
          sizes.thumb,
          checked ? sizes.translate : 'translate-x-0.5',
          'mt-[1px]'
        )}
      />
    </button>
  );

  if (!label && !description) return toggle;

  return (
    <div className="flex items-start gap-3">
      {toggle}
      <div className="flex-1 min-w-0">
        {label && <div className="text-sm font-medium text-text-primary">{label}</div>}
        {description && <div className="text-xs text-text-secondary mt-0.5">{description}</div>}
      </div>
    </div>
  );
}
